<script>
import { mapActions, mapState } from 'vuex';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  FilterOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';
import columns from './columns';

const b = bem('error-logs-page');

export default {
  components: {
    PageHeader,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EyeOutlined,
    FilterOutlined,
  },
  data() {
    return {
      title: 'API Error Logs',
      limit: 20,
      offset: 0,
      selectedRowKeys: [],
      detailModalVisible: false,
      deleteModalVisible: false,
      filters: {
        serviceName: undefined,
        minStatusCode: undefined,
        maxStatusCode: undefined,
        isResolved: 'all',
        startDate: null,
        endDate: null,
        endpoint: '',
        userId: undefined,
      },
      dateRange: [],
      serviceOptions: [
        { value: undefined, label: 'All Services' },
        { value: 'backend', label: 'Backend' },
        { value: 'pm-system-backend', label: 'PM System' },
      ],
      statusOptions: [
        { value: 'all', label: 'All Statuses' },
        { value: 'true', label: 'Resolved' },
        { value: 'false', label: 'Unresolved' },
      ],
      statusCodeOptions: [
        { value: undefined, label: 'All Codes' },
        { value: '4xx', label: '4xx (All Client)' },
        { value: '5xx', label: '5xx (All Server)' },
        { value: 400, label: '400 Bad Request' },
        { value: 401, label: '401 Unauthorized' },
        { value: 403, label: '403 Forbidden' },
        { value: 404, label: '404 Not Found' },
        { value: 409, label: '409 Conflict' },
        { value: 422, label: '422 Validation' },
        { value: 429, label: '429 Too Many Requests' },
        { value: 500, label: '500 Internal Error' },
        { value: 502, label: '502 Bad Gateway' },
        { value: 503, label: '503 Unavailable' },
        { value: 504, label: '504 Gateway Timeout' },
      ],
      selectedStatusCode: undefined,
      statsPeriod: 24,
      statsPeriodOptions: [
        { value: 1, label: 'Last 1 hour' },
        { value: 6, label: 'Last 6 hours' },
        { value: 24, label: 'Last 24 hours' },
        { value: 72, label: 'Last 3 days' },
        { value: 168, label: 'Last 7 days' },
        { value: 720, label: 'Last 30 days' },
      ],
    };
  },
  computed: {
    ...mapState({
      errorLogs: (state) => state.errorLogs.errorLogs,
      total: (state) => state.errorLogs.total,
      loading: (state) => state.errorLogs.loading,
      detail: (state) => state.errorLogs.detail,
      detailLoading: (state) => state.errorLogs.detailLoading,
      stats: (state) => state.errorLogs.stats,
      resolving: (state) => state.errorLogs.resolving,
      deleting: (state) => state.errorLogs.deleting,
    }),
    tableColumns() {
      return columns();
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: this.onSelectChange,
      };
    },
    hasSelected() {
      return this.selectedRowKeys.length > 0;
    },
    statsPeriodLabel() {
      const option = this.statsPeriodOptions.find((o) => o.value === this.statsPeriod);
      return option ? option.label : 'Last 24 hours';
    },
    formattedDetail() {
      if (!this.detail) return null;
      const d = this.detail;
      return {
        ...d,
        correlationId: d.correlationId || d.correlation_id,
        serviceName: d.serviceName || d.service_name,
        statusCode: d.statusCode || d.status_code,
        errorCode: d.errorCode || d.error_code,
        errorMessage: d.errorMessage || d.error_message,
        stackTrace: d.stackTrace || d.stack_trace,
        requestPayload: d.requestPayload || d.request_payload,
        requestHeaders: d.requestHeaders || d.request_headers,
        queryParams: d.queryParams || d.query_params,
        userId: d.userId || d.user_id,
        userRole: d.userRole || d.user_role,
        ipAddress: d.ipAddress || d.ip_address,
        userAgent: d.userAgent || d.user_agent,
        durationMs: d.durationMs || d.duration_ms,
        parentCorrelationId: d.parentCorrelationId || d.parent_correlation_id,
        isResolved: d.isResolved ?? d.is_resolved,
        resolvedAt: d.resolvedAt || d.resolved_at,
        resolvedBy: d.resolvedBy || d.resolved_by,
        resolutionNote: d.resolutionNote || d.resolution_note,
      };
    },
    totalErrors() {
      const services = this.stats?.byService || [];
      return services.reduce((sum, s) => sum + Number(s.totalErrors || s.total_errors || 0), 0);
    },
    serverErrors() {
      const services = this.stats?.byService || [];
      return services.reduce((sum, s) => sum + Number(s.serverErrors || s.server_errors || 0), 0);
    },
    clientErrors() {
      const services = this.stats?.byService || [];
      return services.reduce((sum, s) => sum + Number(s.clientErrors || s.client_errors || 0), 0);
    },
    unresolvedErrors() {
      const services = this.stats?.byService || [];
      return services.reduce(
        (sum, s) => sum + Number(s.unresolvedErrors || s.unresolved_errors || 0),
        0,
      );
    },
    currentFilters() {
      const params = {
        limit: this.limit,
        offset: this.offset,
      };
      if (this.filters.serviceName) {
        params.serviceName = this.filters.serviceName;
      }
      if (this.filters.minStatusCode) {
        params.minStatusCode = this.filters.minStatusCode;
      }
      if (this.filters.maxStatusCode) {
        params.maxStatusCode = this.filters.maxStatusCode;
      }
      if (this.filters.isResolved !== 'all') {
        params.isResolved = this.filters.isResolved;
      }
      if (this.filters.startDate) {
        params.startDate = this.filters.startDate;
      }
      if (this.filters.endDate) {
        params.endDate = this.filters.endDate;
      }
      if (this.filters.endpoint) {
        params.endpoint = this.filters.endpoint;
      }
      if (this.filters.userId) {
        params.userId = this.filters.userId;
      }
      return params;
    },
    formattedLogs() {
      return this.errorLogs.map((log) => ({
        ...log,
        key: log.id,
        serviceName: log.serviceName || log.service_name,
        statusCode: log.statusCode || log.status_code,
        errorMessage: log.errorMessage || log.error_message,
        userId: log.userId || log.user_id,
        durationMs: log.durationMs || log.duration_ms,
        isResolved: log.isResolved ?? log.is_resolved,
        correlationId: log.correlationId || log.correlation_id,
        timestamp: this.formatDate(log.timestamp),
      }));
    },
  },
  methods: {
    ...mapActions({
      getErrorLogs: 'errorLogs/getErrorLogs',
      getErrorLogDetail: 'errorLogs/getErrorLogDetail',
      getErrorStats: 'errorLogs/getErrorStats',
      resolveErrors: 'errorLogs/resolveErrors',
      deleteErrors: 'errorLogs/deleteErrors',
      clearDetail: 'errorLogs/clearDetail',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    },
    fetchLogs() {
      this.getErrorLogs(this.currentFilters);
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },
    onDateRangeChange(dates) {
      if (dates && dates.length === 2) {
        this.filters.startDate = dates[0].startOf('day').toISOString();
        this.filters.endDate = dates[1].endOf('day').toISOString();
      } else {
        this.filters.startDate = null;
        this.filters.endDate = null;
      }
    },
    onStatusCodeChange(value) {
      this.selectedStatusCode = value;
      if (value === '4xx') {
        this.filters.minStatusCode = 400;
        this.filters.maxStatusCode = 499;
      } else if (value === '5xx') {
        this.filters.minStatusCode = 500;
        this.filters.maxStatusCode = 599;
      } else if (typeof value === 'number') {
        this.filters.minStatusCode = value;
        this.filters.maxStatusCode = value;
      } else {
        this.filters.minStatusCode = undefined;
        this.filters.maxStatusCode = undefined;
      }
    },
    applyFilters() {
      this.offset = 0;
      this.selectedRowKeys = [];
      this.fetchLogs();
    },
    resetFilters() {
      this.filters = {
        serviceName: undefined,
        minStatusCode: undefined,
        maxStatusCode: undefined,
        isResolved: 'all',
        startDate: null,
        endDate: null,
        endpoint: '',
        userId: undefined,
      };
      this.dateRange = [];
      this.selectedStatusCode = undefined;
      this.offset = 0;
      this.selectedRowKeys = [];
      this.fetchLogs();
    },
    setPagination(page, pageSize) {
      this.offset = (page - 1) * pageSize;
      this.limit = pageSize;
      this.fetchLogs();
    },
    viewDetail(record) {
      this.getErrorLogDetail(record.id);
      this.detailModalVisible = true;
    },
    closeDetailModal() {
      this.detailModalVisible = false;
      this.clearDetail();
    },
    resolveSelected(isResolved) {
      this.resolveErrors({
        ids: this.selectedRowKeys.map((id) => Number(id)),
        isResolved,
        filters: this.currentFilters,
      });
      this.selectedRowKeys = [];
    },
    deleteSelected() {
      this.deleteErrors({
        payload: { ids: this.selectedRowKeys.map((id) => Number(id)) },
        filters: this.currentFilters,
      });
      this.selectedRowKeys = [];
      this.deleteModalVisible = false;
    },
    getStatusColor(statusCode) {
      if (statusCode >= 500) return '#ff4d4f';
      if (statusCode >= 400) return '#faad14';
      return '#52c41a';
    },
    getMethodColor(method) {
      const colors = {
        GET: '#1890ff',
        POST: '#52c41a',
        PUT: '#faad14',
        PATCH: '#722ed1',
        DELETE: '#ff4d4f',
      };
      return colors[method] || '#666';
    },
    onStatsPeriodChange(hours) {
      this.statsPeriod = hours;
      this.getErrorStats({ hours });
    },
    refreshStats() {
      this.getErrorStats({ hours: this.statsPeriod });
    },
  },
  mounted() {
    this.fetchLogs();
    this.getErrorStats({ hours: this.statsPeriod });
  },
};
</script>

<template>
  <PageHeader :title="title" />
  <div :class="b()">
    <!-- Stats Section -->
    <div :class="b('stats-section')">
      <div :class="b('stats-header')">
        <span :class="b('stats-title')">Statistics</span>
        <a-select
          :value="statsPeriod"
          :options="statsPeriodOptions"
          style="width: 160px"
          @change="onStatsPeriodChange"
        />
        <a-button @click="refreshStats" :loading="loading">
          <template #icon><ReloadOutlined /></template>
        </a-button>
      </div>
      <div :class="b('stats')" v-if="stats">
        <div :class="b('stat-card')">
          <div :class="b('stat-value')">{{ totalErrors }}</div>
          <div :class="b('stat-label')">Total Errors</div>
        </div>
        <div :class="b('stat-card', { type: 'danger' })">
          <div :class="b('stat-value')">{{ serverErrors }}</div>
          <div :class="b('stat-label')">Server Errors (5xx)</div>
        </div>
        <div :class="b('stat-card', { type: 'warning' })">
          <div :class="b('stat-value')">{{ clientErrors }}</div>
          <div :class="b('stat-label')">Client Errors (4xx)</div>
        </div>
        <div :class="b('stat-card', { type: 'info' })">
          <div :class="b('stat-value')">{{ unresolvedErrors }}</div>
          <div :class="b('stat-label')">Unresolved</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div :class="b('filters')">
      <div :class="b('filter-row')">
        <a-range-picker
          v-model:value="dateRange"
          :placeholder="['Start Date', 'End Date']"
          @change="onDateRangeChange"
          style="width: 280px"
        />
        <a-select
          v-model:value="filters.serviceName"
          :options="serviceOptions"
          placeholder="Service"
          style="width: 160px"
        />
        <a-select
          v-model:value="filters.isResolved"
          :options="statusOptions"
          placeholder="Status"
          style="width: 140px"
        />
        <a-select
          v-model:value="selectedStatusCode"
          :options="statusCodeOptions"
          placeholder="Status Code"
          style="width: 180px"
          allowClear
          @change="onStatusCodeChange"
        />
        <a-input
          v-model:value="filters.endpoint"
          placeholder="Endpoint..."
          style="width: 200px"
          @pressEnter="applyFilters"
        />
        <a-input-number
          v-model:value="filters.userId"
          placeholder="User ID"
          style="width: 120px"
        />
        <a-button type="primary" @click="applyFilters">
          <template #icon><FilterOutlined /></template>
          Filter
        </a-button>
        <a-button @click="resetFilters">Reset</a-button>
        <a-button @click="fetchLogs">
          <template #icon><ReloadOutlined /></template>
        </a-button>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div :class="b('bulk-actions')" v-if="hasSelected">
      <span>{{ selectedRowKeys.length }} selected</span>
      <a-button type="primary" @click="resolveSelected(true)" :loading="resolving">
        <template #icon><CheckCircleOutlined /></template>
        Mark Resolved
      </a-button>
      <a-button @click="resolveSelected(false)" :loading="resolving">
        <template #icon><CloseCircleOutlined /></template>
        Mark Unresolved
      </a-button>
      <a-button danger @click="deleteModalVisible = true">
        <template #icon><DeleteOutlined /></template>
        Delete
      </a-button>
    </div>

    <!-- Table -->
    <div :class="b('table')">
      <a-table
        :dataSource="formattedLogs"
        :columns="tableColumns"
        :loading="loading"
        :row-selection="rowSelection"
        :pagination="{
          pageSize: limit,
          total,
          current: Math.floor(offset / limit) + 1,
          onChange: setPagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Total ${total} errors`,
        }"
        :scroll="{ x: 1400 }"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'serviceName'">
            <a-tag :color="record.serviceName === 'backend' ? 'blue' : 'purple'">
              {{ record.serviceName }}
            </a-tag>
          </template>
          <template v-if="column.key === 'method'">
            <a-tag :color="getMethodColor(record.method)">
              {{ record.method }}
            </a-tag>
          </template>
          <template v-if="column.key === 'statusCode'">
            <a-tag :color="getStatusColor(record.statusCode)">
              {{ record.statusCode }}
            </a-tag>
          </template>
          <template v-if="column.key === 'durationMs'">
            <span v-if="record.durationMs">{{ record.durationMs }}ms</span>
            <span v-else>-</span>
          </template>
          <template v-if="column.key === 'isResolved'">
            <a-tag :color="record.isResolved ? 'green' : 'red'">
              {{ record.isResolved ? 'Resolved' : 'Open' }}
            </a-tag>
          </template>
          <template v-if="column.key === 'actions'">
            <a-button type="link" size="small" @click="viewDetail(record)">
              <template #icon><EyeOutlined /></template>
              View
            </a-button>
          </template>
        </template>
      </a-table>
    </div>

    <!-- Detail Modal -->
    <a-modal
      v-model:open="detailModalVisible"
      title="Error Details"
      width="800px"
      :footer="null"
      @cancel="closeDetailModal"
    >
      <a-spin :spinning="detailLoading">
        <div v-if="formattedDetail" :class="b('detail')">
          <a-descriptions bordered :column="2" size="small">
            <a-descriptions-item label="ID">
              {{ formattedDetail.id }}
            </a-descriptions-item>
            <a-descriptions-item label="Correlation ID">
              <code>{{ formattedDetail.correlationId }}</code>
            </a-descriptions-item>
            <a-descriptions-item label="Timestamp">
              {{ formatDate(formattedDetail.timestamp) }}
            </a-descriptions-item>
            <a-descriptions-item label="Service">
              {{ formattedDetail.serviceName }}
            </a-descriptions-item>
            <a-descriptions-item label="Method">
              <a-tag :color="getMethodColor(formattedDetail.method)">
                {{ formattedDetail.method }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="Status Code">
              <a-tag :color="getStatusColor(formattedDetail.statusCode)">
                {{ formattedDetail.statusCode }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="Endpoint" :span="2">
              <code>{{ formattedDetail.endpoint }}</code>
            </a-descriptions-item>
            <a-descriptions-item label="Error Message" :span="2">
              {{ formattedDetail.errorMessage || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="User ID">
              {{ formattedDetail.userId || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="User Role">
              {{ formattedDetail.userRole || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="IP Address">
              {{ formattedDetail.ipAddress || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="Duration">
              {{ formattedDetail.durationMs ? `${formattedDetail.durationMs}ms` : '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="Resolved">
              <a-tag :color="formattedDetail.isResolved ? 'green' : 'red'">
                {{ formattedDetail.isResolved ? 'Yes' : 'No' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="Resolved By" v-if="formattedDetail.isResolved">
              {{ formattedDetail.resolvedBy || '-' }}
            </a-descriptions-item>
          </a-descriptions>

          <div v-if="formattedDetail.stackTrace" :class="b('stack-trace')">
            <h4>Stack Trace</h4>
            <pre>{{ formattedDetail.stackTrace }}</pre>
          </div>

          <div v-if="formattedDetail.requestPayload" :class="b('json-block')">
            <h4>Request Payload</h4>
            <pre>{{ JSON.stringify(formattedDetail.requestPayload, null, 2) }}</pre>
          </div>

          <div v-if="formattedDetail.queryParams" :class="b('json-block')">
            <h4>Query Parameters</h4>
            <pre>{{ JSON.stringify(formattedDetail.queryParams, null, 2) }}</pre>
          </div>

          <div v-if="formattedDetail.requestHeaders" :class="b('json-block')">
            <h4>Request Headers</h4>
            <pre>{{ JSON.stringify(formattedDetail.requestHeaders, null, 2) }}</pre>
          </div>

          <div v-if="formattedDetail.resolutionNote" :class="b('resolution-note')">
            <h4>Resolution Note</h4>
            <p>{{ formattedDetail.resolutionNote }}</p>
          </div>
        </div>
      </a-spin>
    </a-modal>

    <!-- Delete Confirmation Modal -->
    <a-modal
      v-model:open="deleteModalVisible"
      title="Confirm Delete"
      @ok="deleteSelected"
      :okButtonProps="{ danger: true }"
      okText="Delete"
    >
      <p>Are you sure you want to delete {{ selectedRowKeys.length }} error log(s)?</p>
      <p>This action cannot be undone.</p>
    </a-modal>
  </div>
</template>

<style src="./style.scss" scoped lang="scss"></style>
